import {useAppSelector} from '../../../store/store';
import {Button} from '../../../components/button/button';
import {ButtonColorType} from '../../../enums/button_color_type.ts';
import {Status} from '../../../enums/status.ts';
import {CenteredSpinner} from '../../../components/spinner/centered_spinner.tsx';
import {VehicleType} from '../../../gql/pwa/graphql.ts';
import {format} from 'date-fns';
import {nl} from 'date-fns/locale';
import {__} from '../../../support/lang/translations.ts';
import buttonStyles from '../../../components/button/button.module.scss';
import {VehicleListItem} from '../../../components/vehicle/vehicle_list_item.tsx';
import {List} from '../../../components/list/list.tsx';
import formStyles from '../../../components/form/form.module.scss';
import {Spacer} from '../../../components/utility/spacer.tsx';
import {classNames} from '../../../support/classnames.ts';
import {ShowElement} from '../../../components/utility/show_element_wrapper.tsx';
import {HideElement} from '../../../components/utility/hide_element_wrapper.tsx';
import {useNavigation} from 'react-router-dom';

export function SummaryTitle() {
    return (
        <>
            <h1>{__('appointment.summary.title')}</h1>
            <h2>{__('appointment.summary.subtitle')}</h2>
        </>
    );
}

interface ContentProps {
    vehicle: VehicleType | null;
    locationId: number | null;
    mileage: number | null;
    mainActivityId: string | null;
    subActivityIds: string[];
    replacementVehicleId: string | null;
    date: Date | null;
    time: string | null;
    remarks: string | null;
}

export function SummaryContent(props: ContentProps) {
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const appConfig = useAppSelector((state) => state.appConfig);
    const location = appConfig.locations?.find((location) => location?.id === props.locationId);
    const specialty = location?.specialties?.find(() => true);
    const activities = specialty?.activities || [];
    const replacementVehicles = specialty?.replacement_vehicles || [];
    const hasMainActivities = activities.some((a) => a?.type === 'main');
    const mainActivity = activities.find((a) => a?.id === props.mainActivityId);
    const subActivities = activities.filter((a) => props.subActivityIds.find((id) => a?.id === id)).map((a) => a?.name);
    const replacementVehicle = replacementVehicles.find((rv) => rv?.id === props.replacementVehicleId);

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    return (
        <>
            <h3>{__('appointment.summary.title_appointment')}</h3>

            <div className={classNames(formStyles.form, formStyles.form_centered)}>
                <div className={formStyles.form__grid}>
                    <div className={formStyles.grid__custom}>
                        <div data-grid-column-start={1} data-grid-column-end={13}>
                            <List>
                                <VehicleListItem vehicle={props.vehicle} />
                            </List>
                            <Spacer />
                        </div>
                        <fieldset>
                            <label data-grid-column-start={1} data-grid-column-end={13}>
                                {__('appointment.summary.fields.mileage')}
                                <div className={formStyles.input_inactive}>{props.mileage}</div>
                            </label>
                        </fieldset>

                        {hasMainActivities && (
                            <fieldset>
                                <label data-grid-column-start={1} data-grid-column-end={13}>
                                    {__('appointment.summary.fields.main_activity')}
                                    <div className={formStyles.input_inactive}>{mainActivity?.name}</div>
                                </label>
                            </fieldset>
                        )}

                        {subActivities.length > 0 && (
                            <fieldset>
                                <label data-grid-column-start={1} data-grid-column-end={13}>
                                    {hasMainActivities
                                        ? __('appointment.summary.fields.sub_activities')
                                        : __('appointment.summary.fields.activities')}
                                    <div className={formStyles.input_inactive}>{subActivities.join(', ')}</div>
                                </label>
                            </fieldset>
                        )}

                        <fieldset>
                            <label data-grid-column-start={1} data-grid-column-end={13}>
                                {__('appointment.summary.fields.replacement_vehicle')}
                                <div className={formStyles.input_inactive}>{replacementVehicle?.name}</div>
                            </label>
                        </fieldset>

                        <fieldset>
                            <label data-grid-column-start={1} data-grid-column-end={13}>
                                {__('appointment.summary.fields.date_and_time')}
                                <div className={formStyles.input_inactive}>
                                    {props.date &&
                                        __('appointment.summary.date_and_time_value', {
                                            date: format(props.date, 'd MMMM yyyy', {locale: nl}),
                                            time: props.time ?? '',
                                        })}
                                </div>
                            </label>
                        </fieldset>

                        {props.remarks != null && props.remarks !== '' && (
                            <fieldset>
                                <label data-grid-column-start={1} data-grid-column-end={13}>
                                    {__('appointment.summary.fields.remarks')}
                                    <div className={formStyles.input_inactive}>{props.remarks}</div>
                                </label>
                            </fieldset>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

interface FooterProps {
    onPreviousSlide: () => void;
}

export function SummaryFooter(props: FooterProps) {
    const navigation = useNavigation();
    const isSubmitting = navigation.state === 'submitting' || navigation.state === 'loading';

    return (
        <>
            <ShowElement size={'sm'}>
                <Button
                    className={buttonStyles.button_step}
                    type="button"
                    onClick={() => props.onPreviousSlide()}
                    colorType={ButtonColorType.Black}
                    disabled={isSubmitting}
                >
                    <i className="fa-light fa-chevron-left" />
                    <span>{__('appointment.summary.footer.buttons.previous')}</span>
                </Button>
            </ShowElement>

            <HideElement size={'sm'}>
                <span />
            </HideElement>

            <Button
                className={buttonStyles.button_step}
                type="submit"
                colorType={ButtonColorType.Green}
                disabled={isSubmitting}
                loading={isSubmitting}
            >
                <i className={isSubmitting ? 'fa-light fa-arrow-rotate-right' : 'fa-light fa-check'} />
                <span>{__('appointment.summary.footer.buttons.submit')}</span>
            </Button>
        </>
    );
}
